var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Schema, type } from "@colyseus/schema";
import { BARRACKS_AIM_NONE } from "../types/buildings.js";
/**
 * Synced mutable state for one movement node, keyed by its graph `id`.
 *
 * The node topology (positions, edges, zone) is deterministic from
 * `BOARD_RADIUS` and is regenerated on both client and server via `buildBoard`
 * — only this per-node state travels over the wire.
 */
export class NodeState extends Schema {
    constructor() {
        super(...arguments);
        this.id = 0;
        /** Session id of the player owning the building here, or "" if none. */
        this.ownerId = "";
        /** "" | "scout" | "barracks" | "mine" | "bridge". */
        this.building = "";
        /** The node's printed resource ("" if none) — relevant to Mines. */
        this.resource = "";
        /** "" | "permanent" | "oneTime" — how `resource` behaves when worked. */
        this.permanence = "";
        /** True when this node's paths are severed (needs a Bridge to cross). */
        this.disconnected = false;
        /** Node id a Barracks here is aimed at, or BARRACKS_AIM_NONE when upright.
         * Aiming commits the Barracks until the round ends (all aims clear then) or a
         * card effect readies it; an upright Barracks defends itself with 1. */
        this.aimTargetId = BARRACKS_AIM_NONE;
        /** The aim's posture: "attack" | "defend", or "" when upright. Set and cleared
         * together with `aimTargetId`. */
        this.aimMode = "";
        /** Canonical `"lo-hi"` edge key of the BRIDGE a Barracks here is aimed at, or
         * "" for a node aim (or upright). When set, `aimTargetId` holds an in-range
         * endpoint of that edge as the anchor — readers that treat `aimTargetId` as
         * the target NODE must skip nodes with a non-empty `aimEdge`. */
        this.aimEdge = "";
        /** True if this building can't be destroyed (Scout indestructibility effect). */
        this.indestructible = false;
        /** Session id of the player whose Fortress occupies this node (a pattern
         * building or an X marker), or "". Hardened: another player's fortress X may
         * never land here (no demolition of fortress nodes). A Barracks on such a
         * node is absorbed into the fortress: it can no longer ATTACK, only be aimed
         * to defend — attack resolution must reject attackers from these nodes. */
        this.fortressOwnerId = "";
        /** True if this node is a player's home base (the seat they placed during
         * setup). Owner is `ownerId`; this just flags it for a distinct board marker. */
        this.isHomeBase = false;
        /** Session id of the player this building was CAPTURED from (Church of
         * Penguin converting a fortress takes its pieces — `ownerId` becomes the
         * captor's for every rule), or "". Purely cosmetic: the client keeps
         * painting the piece in the original owner's colour. A Corrupt Hermit
         * conscription sets nothing here on catalogue "45"+ — it borrows the piece
         * without taking it. */
        this.capturedFromId = "";
        /** A PLACED one-time resource kind (Pee-Pee Pinata's distribution), or
         * "". Independent of the printed `resource`/`permanence`: building here
         * collects `bonusAmount` of this kind IN ADDITION to whatever the printed
         * resource grants (both one-time and permanent stack with it). */
        this.bonusResource = "";
        /** Units of `bonusResource` waiting here (0 when none). */
        this.bonusAmount = 0;
    }
}
__decorate([
    type("uint16")
], NodeState.prototype, "id", void 0);
__decorate([
    type("string")
], NodeState.prototype, "ownerId", void 0);
__decorate([
    type("string")
], NodeState.prototype, "building", void 0);
__decorate([
    type("string")
], NodeState.prototype, "resource", void 0);
__decorate([
    type("string")
], NodeState.prototype, "permanence", void 0);
__decorate([
    type("boolean")
], NodeState.prototype, "disconnected", void 0);
__decorate([
    type("int16")
], NodeState.prototype, "aimTargetId", void 0);
__decorate([
    type("string")
], NodeState.prototype, "aimMode", void 0);
__decorate([
    type("string")
], NodeState.prototype, "aimEdge", void 0);
__decorate([
    type("boolean")
], NodeState.prototype, "indestructible", void 0);
__decorate([
    type("string")
], NodeState.prototype, "fortressOwnerId", void 0);
__decorate([
    type("boolean")
], NodeState.prototype, "isHomeBase", void 0);
__decorate([
    type("string")
], NodeState.prototype, "capturedFromId", void 0);
__decorate([
    type("string")
], NodeState.prototype, "bonusResource", void 0);
__decorate([
    type("uint8")
], NodeState.prototype, "bonusAmount", void 0);
//# sourceMappingURL=NodeState.js.map